'use client';

import { flexRender, type RowData } from '@tanstack/react-table';
import type { Header, Table as TanstackTable } from '@/lib/table-features';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { useLocale } from 'next-intl';
import * as React from 'react';
import { ChevronDown, ChevronRight, GripVertical } from 'lucide-react';

import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { DataTablePagination } from '@/components/ui/table/data-table-pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { getCommonPinningStyles } from '@/lib/data-table';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

interface DataTableProps<
  TData extends RowData
> extends React.ComponentProps<'div'> {
  table: TanstackTable<TData>;
  actionBar?: React.ReactNode;
  onRowClick?: (row: TData) => void;
  /** Render expanded row content. Passing this enables row expansion. */
  renderExpandedRow?: (row: TData) => React.ReactNode;
}

// ─── Sortable Header Cell ────────────────────────────────────────────
function SortableHeaderCell<TData extends RowData>({
  header,
  minColWidth,
  isRtl
}: {
  header: Header<TData, unknown>;
  minColWidth?: number;
  isRtl: boolean;
}) {
  const { column } = header;
  const isPinned = column.getIsPinned();
  const isFixed =
    column.id === 'select' || column.id === 'actions' || column.id === 'expand';
  const isDraggable = !isFixed && !isPinned;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: column.id,
    disabled: !isDraggable
  });

  const style: React.CSSProperties = {
    ...getCommonPinningStyles({
      column,
      isHeader: true,
      minColWidth,
      isRtl
    }),
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
    zIndex: isDragging ? 2 : isPinned ? 1 : undefined
  };

  return (
    <TableHead
      ref={setNodeRef}
      colSpan={header.colSpan}
      style={style}
      className={cn(isDragging && 'bg-muted')}
    >
      <div className='flex items-center gap-0.5'>
        {isDraggable && (
          <button
            type='button'
            className='text-muted-foreground/50 hover:text-muted-foreground -ms-1 cursor-grab opacity-0 transition-opacity group-hover/header:opacity-100 active:cursor-grabbing'
            {...attributes}
            {...listeners}
          >
            <GripVertical className='size-3.5' />
          </button>
        )}
        <div className='flex-1'>
          {header.isPlaceholder ? null : !isFixed &&
            typeof header.column.columnDef.header === 'string' ? (
            <DataTableColumnHeader
              column={header.column}
              title={header.column.columnDef.header}
            />
          ) : (
            flexRender(header.column.columnDef.header, header.getContext())
          )}
        </div>
        {!isFixed && (
          <div
            onDoubleClick={() => column.resetSize()}
            onMouseDown={header.getResizeHandler()}
            onTouchStart={header.getResizeHandler()}
            className={cn(
              'absolute end-0 top-0 h-full w-1 cursor-col-resize touch-none opacity-0 transition-opacity select-none group-hover/header:opacity-100 hover:opacity-100',
              column.getIsResizing() ? 'bg-primary opacity-100' : 'bg-border'
            )}
          />
        )}
      </div>
    </TableHead>
  );
}

// ─── Main DataTable ──────────────────────────────────────────────────
export function DataTable<TData extends RowData>({
  table,
  actionBar,
  onRowClick,
  renderExpandedRow,
  children
}: DataTableProps<TData>) {
  const locale = useLocale();
  const isRtl = locale === 'ar';

  const [focusedCell, setFocusedCell] = React.useState<{
    row: number;
    col: number;
  } | null>(null);

  // Measure column widths after data loads. Before measurement, no width
  // constraints are applied so the browser auto-sizes columns based on content.
  const headerRowNode = React.useRef<HTMLTableRowElement | null>(null);
  const headerRowCallback = React.useCallback(
    (node: HTMLTableRowElement | null) => {
      headerRowNode.current = node;
    },
    []
  );
  const [columnsMeasured, setColumnsMeasured] = React.useState(false);
  const [sizingCleared, setSizingCleared] = React.useState(false);
  const [columnMinWidths, setColumnMinWidths] = React.useState<
    Record<string, number>
  >({});
  // Holds pinned column sizes preserved across the clear→measure cycle
  const preservedSizingRef = React.useRef<Record<string, number>>({});

  const rows = table.getRowModel().rows;

  // Phase 1: clear stale sizing so the table auto-sizes columns based on
  // content.  Pinned columns keep their existing size — they were originally
  // measured at the correct auto-layout width before being pinned, and
  // re-measuring them while constrained by getCommonPinningStyles gives a
  // wrong (smaller) value because CSS min-width is unreliable on <th>.
  React.useEffect(() => {
    if (columnsMeasured || sizingCleared || rows.length === 0) return;

    const pinning = table.state.columnPinning;
    const pinnedIds = new Set([
      ...(pinning.start ?? []),
      ...(pinning.end ?? [])
    ]);
    const currentSizing = table.state.columnSizing;

    const preserved: Record<string, number> = {};
    for (const [id, size] of Object.entries(currentSizing)) {
      if (pinnedIds.has(id)) preserved[id] = size;
    }
    preservedSizingRef.current = preserved;

    // Clear non-pinned sizing; keep pinned column widths intact
    table.setColumnSizing(() => preserved);
    setSizingCleared(true);
  }, [table, columnsMeasured, sizingCleared, rows.length]);

  // Phase 2: measure column widths. useLayoutEffect guarantees the DOM
  // reflects the sizing from Phase 1. Reading offsetWidth forces a
  // synchronous reflow against the committed DOM.
  React.useLayoutEffect(() => {
    if (!sizingCleared || columnsMeasured) return;

    const headerRow = headerRowNode.current;
    if (!headerRow) return;

    const preserved = preservedSizingRef.current;
    const cells = headerRow.querySelectorAll('th');
    // Use headerGroup.headers — NOT getVisibleLeafColumns().
    // getVisibleLeafColumns() reorders columns for pinning (left-pinned
    // first), but the DOM <th> elements stay in definition order.
    // Index-based mapping with the wrong array assigns widths to wrong columns.
    const headers = table.getHeaderGroups()[0]?.headers ?? [];
    const sizing: Record<string, number> = {};
    const mins: Record<string, number> = {};
    // Fixed columns have a flex div that fills the entire cell, so
    // scrollWidth equals the current cell width. Measuring + 16 would cause
    // the column to grow by 16px on every mount when the size is persisted.
    const fixedIds = new Set(['select', 'actions', 'expand']);
    cells.forEach((cell, i) => {
      const header = headers[i];
      if (!header) return;
      const col = header.column;

      if (fixedIds.has(col.id)) {
        // Keep fixed columns at their defined/preserved size — never measure.
        sizing[col.id] = preserved[col.id] ?? col.columnDef.size ?? 20;
        return;
      }

      const content = cell.querySelector('div');
      const contentWidth = content ? content.scrollWidth + 16 : 48;
      mins[col.id] = contentWidth;

      if (col.getIsPinned() && preserved[col.id]) {
        // Pinned column: keep preserved width, but grow if header content
        // is now wider (e.g. locale change)
        sizing[col.id] = Math.max(preserved[col.id], contentWidth);
      } else {
        sizing[col.id] = cell.offsetWidth;
      }
    });
    setColumnMinWidths(mins);
    if (Object.keys(sizing).length > 0) {
      table.setColumnSizing(() => sizing);
    }
    setColumnsMeasured(true);
  }, [sizingCleared, columnsMeasured, table]);

  // Phase 0: re-run the measure cycle whenever the natural layout could have
  // changed. The phases above lock pinned-column widths ONCE and then guard on
  // `columnsMeasured`, so without this the table keeps stale widths after any
  // in-table event — pagination, filtering, sorting (which swap the rows), a
  // column being shown/hidden/reordered/pinned, or a locale / view-keys switch
  // (which changes header text). Rebuilding a key from that state and diffing
  // it against the last measured key re-arms the clear→measure cycle. Setting
  // column sizing does not change any input here, so this cannot loop.
  const remeasureKey = JSON.stringify({
    columns: table.getVisibleLeafColumns().map((c) => c.id),
    locale,
    pinning: table.state.columnPinning,
    pagination: table.state.pagination,
    sorting: table.state.sorting,
    filters: table.state.columnFilters,
    rowCount: rows.length
  });
  const lastRemeasureKey = React.useRef(remeasureKey);
  React.useEffect(() => {
    if (lastRemeasureKey.current === remeasureKey) return;
    lastRemeasureKey.current = remeasureKey;
    setColumnsMeasured(false);
    setSizingCleared(false);
    setColumnMinWidths({});
  }, [remeasureKey]);

  const columnOrder = table.state.columnOrder;
  const columnIds = React.useMemo(() => {
    if (columnOrder.length > 0) return columnOrder;
    return table.getAllLeafColumns().map((col) => col.id);
  }, [columnOrder, table]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    }),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = columnIds.indexOf(active.id as string);
      const newIndex = columnIds.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;

      const newOrder = [...columnIds];
      newOrder.splice(oldIndex, 1);
      newOrder.splice(newIndex, 0, active.id as string);
      table.setColumnOrder(newOrder);
    },
    [columnIds, table]
  );

  const visibleColumns = table.getVisibleLeafColumns();

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!rows.length) return;

      const maxRow = rows.length - 1;
      const maxCol = visibleColumns.length - 1;

      if (e.key === 'Escape') {
        setFocusedCell(null);
        table.resetRowSelection();
        return;
      }

      if (!focusedCell) {
        if (
          e.key === 'ArrowDown' ||
          e.key === 'ArrowUp' ||
          e.key === 'ArrowLeft' ||
          e.key === 'ArrowRight'
        ) {
          e.preventDefault();
          setFocusedCell({ row: 0, col: 0 });
        }
        return;
      }

      const { row, col } = focusedCell;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedCell({ row: Math.min(row + 1, maxRow), col });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedCell({ row: Math.max(row - 1, 0), col });
          break;
        case 'ArrowRight':
          e.preventDefault();
          setFocusedCell({ row, col: Math.min(col + 1, maxCol) });
          break;
        case 'ArrowLeft':
          e.preventDefault();
          setFocusedCell({ row, col: Math.max(col - 1, 0) });
          break;
        case 'Enter':
          e.preventDefault();
          if (onRowClick) {
            onRowClick(rows[row]!.original);
          }
          break;
      }
    },
    [rows, visibleColumns.length, focusedCell, onRowClick, table]
  );

  return (
    <div className='flex flex-1 flex-col space-y-4'>
      {children}
      <div className='relative flex flex-1'>
        <div className='absolute inset-0 flex overflow-hidden rounded-lg border'>
          <ScrollArea className='h-full w-full [&_[data-slot=table-container]]:overflow-visible'>
            <div
              tabIndex={0}
              role='grid'
              onKeyDown={handleKeyDown}
              className='outline-none'
            >
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToHorizontalAxis]}
                onDragEnd={handleDragEnd}
              >
                <Table>
                  <TableHeader className='bg-muted sticky top-0 z-10'>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow
                        ref={headerRowCallback}
                        key={headerGroup.id}
                        className='group/header'
                      >
                        <SortableContext
                          items={columnIds}
                          strategy={horizontalListSortingStrategy}
                        >
                          {headerGroup.headers.map((header) => (
                            <SortableHeaderCell
                              key={header.id}
                              header={header}
                              minColWidth={columnMinWidths[header.column.id]}
                              isRtl={isRtl}
                            />
                          ))}
                        </SortableContext>
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {rows.length ? (
                      rows.map((row, rowIndex) => (
                        <React.Fragment key={row.id}>
                          <TableRow
                            data-state={row.getIsSelected() && 'selected'}
                            className={cn(
                              onRowClick && 'cursor-pointer',
                              focusedCell?.row === rowIndex &&
                                'ring-primary/50 ring-1 ring-inset'
                            )}
                            onClick={() => onRowClick?.(row.original)}
                          >
                            {row.getVisibleCells().map((cell, colIndex) => {
                              const colId = cell.column.id;
                              const isFixed =
                                colId === 'select' ||
                                colId === 'actions' ||
                                colId === 'expand';
                              const isPinned = cell.column.getIsPinned();
                              // Only pinned (fixed-width) data columns clip
                              // their content to the cell. Non-pinned columns
                              // are auto-sized, so they must render their value
                              // directly and let the column grow to fit —
                              // clipping them is what previously hid the data.
                              const needsClip =
                                columnsMeasured && !!isPinned && !isFixed;
                              return (
                                <TableCell
                                  key={cell.id}
                                  style={{
                                    ...getCommonPinningStyles({
                                      column: cell.column,
                                      minColWidth: columnMinWidths[colId],
                                      isRtl
                                    })
                                  }}
                                  className={cn(
                                    focusedCell?.row === rowIndex &&
                                      focusedCell?.col === colIndex &&
                                      'ring-primary ring-2 ring-inset'
                                  )}
                                >
                                  {needsClip ? (
                                    <div className='w-0 min-w-full overflow-hidden text-ellipsis'>
                                      {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                      )}
                                    </div>
                                  ) : (
                                    flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext()
                                    )
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                          {renderExpandedRow && row.getIsExpanded() && (
                            <TableRow className='bg-muted/30 hover:bg-muted/30'>
                              <TableCell
                                colSpan={row.getVisibleCells().length}
                                className='p-4'
                              >
                                <div className='animate-in fade-in-0 slide-in-from-top-1 duration-200'>
                                  {renderExpandedRow(row.original)}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={table.getAllColumns().length}
                          className='h-24 text-center'
                        >
                          No results.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </DndContext>
            </div>
            <ScrollBar orientation='horizontal' />
          </ScrollArea>
        </div>
      </div>
      <div dir='ltr' className='flex flex-col gap-2.5'>
        <DataTablePagination table={table} />
        {actionBar &&
          table.getFilteredSelectedRowModel().rows.length > 0 &&
          actionBar}
      </div>
    </div>
  );
}

// ─── Expand Column Helper ────────────────────────────────────────────
/**
 * Creates a column definition for row expansion.
 * Add this as the second column (after `select`) in your columns array.
 *
 * @example
 * ```tsx
 * import { getExpandColumn } from '@/components/ui/table/data-table';
 * const columns = [selectColumn, getExpandColumn<MyType>(t), ...dataColumns];
 * ```
 */
export function getExpandColumn<TData extends RowData>(
  tc: (key: string) => string
) {
  return {
    id: 'expand',
    header: ({ table: tbl }: { table: TanstackTable<TData> }) => (
      <button
        type='button'
        onClick={() => tbl.toggleAllRowsExpanded()}
        className='text-muted-foreground hover:text-foreground flex items-center'
        aria-label={
          tbl.getIsAllRowsExpanded() ? tc('collapseAll') : tc('expandAll')
        }
      >
        {tbl.getIsAllRowsExpanded() ? (
          <ChevronDown className='size-4' />
        ) : (
          <ChevronRight className='size-4' />
        )}
      </button>
    ),
    cell: ({
      row
    }: {
      row: { getIsExpanded: () => boolean; toggleExpanded: () => void };
    }) => (
      <button
        type='button'
        onClick={(e) => {
          e.stopPropagation();
          row.toggleExpanded();
        }}
        className='text-muted-foreground hover:text-foreground flex items-center'
        aria-label={row.getIsExpanded() ? tc('collapseRow') : tc('expandRow')}
      >
        {row.getIsExpanded() ? (
          <ChevronDown className='size-4' />
        ) : (
          <ChevronRight className='size-4' />
        )}
      </button>
    ),
    enableSorting: false,
    enableHiding: false,
    enablePinning: false,
    size: 32
  };
}
